/**
 * Medical Summary Export Service
 * 
 * Handles downloading the medical summary PDF for doctor visits.
 * Supports both authenticated API calls and demo mode fallback.
 */

import API from '../utils/api';

/**
 * Export medical summary as PDF
 * 
 * For authenticated users, this downloads a server-generated PDF.
 * For demo mode, it generates a client-side summary.
 * 
 * @param {Object} options - Export options
 * @param {boolean} options.isDemoMode - Whether app is in demo mode
 * @param {Object} options.demoData - Demo data for client-side generation
 * @returns {Promise<boolean>} - Success status
 */
export async function exportMedicalSummaryPDF({ isDemoMode, demoData } = {}) {
  try {
    if (isDemoMode) {
      // In demo mode, generate a simple printable HTML that opens in new window
      return generateDemoPDF(demoData);
    }

    // Fetch the PDF from the backend
    const response = await API.get('/medical/export-summary-pdf', {
      responseType: 'blob',
    });

    // Create download link
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    
    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'Medical_Summary.pdf';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    // Trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Failed to export medical summary:', error);
    throw error;
  }
}

/**
 * Generate a printable demo PDF using a new window
 * 
 * @param {Object} demoData - Demo medical data
 * @returns {boolean} - Success status
 */
function generateDemoPDF(demoData) {
  const medical = demoData?.medical || {};
  const profile = demoData?.profile || {};
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Generate HTML content
  const htmlContent = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Medical Summary - ${profile.fullName || 'Patient'}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { 
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
          line-height: 1.5; 
          color: #1e293b;
          padding: 40px;
          max-width: 800px;
          margin: 0 auto;
        }
        h1 { 
          color: #059669; 
          text-align: center; 
          font-size: 28px; 
          margin-bottom: 8px;
        }
        .subtitle { 
          text-align: center; 
          color: #64748b; 
          margin-bottom: 24px;
          font-size: 14px;
        }
        .divider { 
          border-bottom: 1px solid #e2e8f0; 
          margin: 16px 0;
        }
        h2 { 
          color: #0f172a; 
          font-size: 16px; 
          margin: 24px 0 12px;
          padding-bottom: 4px;
          border-bottom: 2px solid #e2e8f0;
        }
        h2.allergy { color: #dc2626; border-color: #fecaca; }
        h2.medication { color: #7c3aed; border-color: #e9d5ff; }
        h2.condition { color: #d97706; border-color: #fde68a; }
        h2.provider { color: #059669; border-color: #a7f3d0; }
        h2.contact { color: #db2777; border-color: #fbcfe8; }
        table { 
          width: 100%; 
          border-collapse: collapse; 
          margin: 8px 0 16px;
          font-size: 13px;
        }
        th { 
          text-align: left; 
          padding: 8px 12px; 
          background: #f8fafc;
          font-weight: 600;
          border: 1px solid #e2e8f0;
        }
        td { 
          padding: 8px 12px; 
          border: 1px solid #e2e8f0;
          vertical-align: top;
        }
        .allergy-table th { background: #fee2e2; color: #dc2626; }
        .allergy-table td, .allergy-table th { border-color: #fecaca; }
        .medication-table th { background: #f3e8ff; color: #7c3aed; }
        .medication-table td, .medication-table th { border-color: #e9d5ff; }
        .condition-table th { background: #fef3c7; color: #d97706; }
        .condition-table td, .condition-table th { border-color: #fde68a; }
        .provider-table th { background: #d1fae5; color: #059669; }
        .provider-table td, .provider-table th { border-color: #a7f3d0; }
        .contact-table th { background: #fce7f3; color: #db2777; }
        .contact-table td, .contact-table th { border-color: #fbcfe8; }
        .info-row { display: flex; gap: 8px; margin: 4px 0; }
        .info-label { color: #64748b; min-width: 120px; }
        .info-value { font-weight: 500; }
        .empty { color: #94a3b8; font-style: italic; font-size: 13px; }
        .footer { 
          margin-top: 32px; 
          padding-top: 16px; 
          border-top: 1px solid #e2e8f0;
          text-align: center;
          color: #94a3b8;
          font-size: 11px;
        }
        .demo-banner {
          background: #fef3c7;
          color: #d97706;
          padding: 8px 16px;
          text-align: center;
          border-radius: 6px;
          margin-bottom: 24px;
          font-size: 13px;
        }
        @media print {
          body { padding: 20px; }
          .demo-banner { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="demo-banner">Demo Mode - Sample Medical Summary</div>
      
      <h1>Medical Summary</h1>
      <p class="subtitle">Prepared for <strong>${profile.fullName || 'Michael Carter'}</strong></p>
      
      <div class="divider"></div>
      
      <!-- Basic Information -->
      <h2>Basic Information</h2>
      <div class="info-row"><span class="info-label">Date of Birth:</span><span class="info-value">${formatDate(profile.dateOfBirth || '1979-06-15')}</span></div>
      <div class="info-row"><span class="info-label">Blood Type:</span><span class="info-value">${profile.bloodType || 'O+'}</span></div>
      <div class="info-row"><span class="info-label">Organ Donor:</span><span class="info-value">${profile.organDonor ? 'Yes' : 'No'}</span></div>
      
      <!-- Allergies -->
      <h2 class="allergy">⚠ Allergies</h2>
      ${medical.allergies?.length ? `
        <table class="allergy-table">
          <thead><tr><th>Allergen</th><th>Reaction</th><th>Severity</th></tr></thead>
          <tbody>
            ${medical.allergies.map(a => `
              <tr>
                <td>${a.name || a.allergen || '—'}</td>
                <td>${a.reaction || '—'}</td>
                <td>${(a.severity || '').charAt(0).toUpperCase() + (a.severity || '').slice(1)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      ` : '<p class="empty">No known allergies recorded.</p>'}
      
      <!-- Medications -->
      <h2 class="medication">Current Medications</h2>
      ${medical.medications?.length ? `
        <table class="medication-table">
          <thead><tr><th>Medication</th><th>Dosage</th><th>Frequency</th><th>Purpose</th></tr></thead>
          <tbody>
            ${medical.medications.filter(m => m.is_active !== false).map(m => `
              <tr>
                <td>${m.name || '—'}</td>
                <td>${m.dosage || '—'}</td>
                <td>${m.frequency || '—'}</td>
                <td>${m.prescribed_for || m.reason || m.purpose || '—'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      ` : '<p class="empty">No current medications recorded.</p>'}
      
      <!-- Medical Conditions -->
      <h2 class="condition">Medical Conditions</h2>
      ${medical.conditions?.length ? `
        <table class="condition-table">
          <thead><tr><th>Condition</th><th>Status</th><th>Treatment</th></tr></thead>
          <tbody>
            ${medical.conditions.map(c => `
              <tr>
                <td>${c.condition || c.name || '—'}</td>
                <td>${(c.status || 'Active').charAt(0).toUpperCase() + (c.status || 'Active').slice(1)}</td>
                <td>${c.treatment || '—'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      ` : '<p class="empty">No medical conditions recorded.</p>'}
      
      <!-- Healthcare Providers -->
      <h2 class="provider">Healthcare Providers</h2>
      ${medical.doctors?.length ? `
        <table class="provider-table">
          <thead><tr><th>Provider</th><th>Specialty</th><th>Clinic</th><th>Phone</th></tr></thead>
          <tbody>
            ${medical.doctors.map(d => `
              <tr>
                <td>${d.name || '—'}</td>
                <td>${d.specialty || '—'}</td>
                <td>${d.clinic || d.practice || '—'}</td>
                <td>${d.phone || '—'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      ` : '<p class="empty">No healthcare providers recorded.</p>'}
      
      <!-- Emergency Contacts -->
      <h2 class="contact">Emergency Contacts</h2>
      ${medical.emergencyContacts?.length || demoData?.emergencyContacts?.length ? `
        <table class="contact-table">
          <thead><tr><th>Name</th><th>Relationship</th><th>Phone</th></tr></thead>
          <tbody>
            ${(medical.emergencyContacts || demoData?.emergencyContacts || []).map(c => `
              <tr>
                <td>${c.name || '—'}</td>
                <td>${c.relationship || '—'}</td>
                <td>${c.phone || '—'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      ` : '<p class="empty">No emergency contacts recorded.</p>'}
      
      <div class="footer">
        <p>Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })} • Forget Me Not App</p>
        <p>This document is for informational purposes only. Please consult your healthcare provider for medical advice.</p>
      </div>
      
      <script>
        // Auto-open print dialog
        window.onload = function() {
          window.print();
        };
      </script>
    </body>
    </html>
  `;

  // Open in new window for printing
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    return true;
  }
  
  return false;
}

/**
 * Get medical summary data for preview
 * 
 * @returns {Promise<Object>} - Medical summary data
 */
export async function getMedicalSummaryData() {
  const response = await API.get('/medical/summary-data');
  return response.data;
}

export default {
  exportMedicalSummaryPDF,
  getMedicalSummaryData,
};
